import { useQuery } from "@tanstack/react-query";
import { getDoc, QuerySnapshot } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import {
  getAllColaboradoresAtivosENaoExcluidos,
  getColaboradorDocumentReferenceFromPath
} from "@/modules/colaboradores/firestore";
import { getComentariosWithDateFilter } from "@/modules/ideia-comentarios/firestore";
import { getIdeiasPublishedWithDateFilter } from "@/modules/ideias/firestore";
import { getSumMoedasPerIdeia } from "@/modules/ideias-activities-per-colaborador/firestore";
import { getLikes } from "@/modules/ideias-likes/firestore";
import { getRegulamentoDocumentFromReferencePath } from "@/modules/regulamento-ideias/firestore";
import useUserStore from "@/modules/users/store";
import { Cards } from "@/src/routes/ideias-relatorio-visao-geral/IdeiasRelatorioVisaoGeralPage";
import { FiltroIdeia } from "../components/ModalFilterIdeias";

// Named constants to replace magic numbers
const CEM_PORCENTO = 100;
const CACHE_STALE_TIME_MINUTES = 5;
const CACHE_STALE_TIME_SECONDS = 60;
const CACHE_STALE_TIME_MILLISECONDS = 1000;
const STALE_TIME_MS = CACHE_STALE_TIME_MINUTES * CACHE_STALE_TIME_SECONDS * CACHE_STALE_TIME_MILLISECONDS;
const EPOCH_DATE = new Date(0);
const ERRO_FETCH_COLABORADOR = "Falha ao buscar colaborador:";
const ERRO_FETCH_REGULAMENTO = "Falha ao buscar regulamento:";
const VALOR_NAO_ENCONTRADO = "Não encontrado";
const CAMPO_TITULO = "Título";
const CAMPO_AUTORES = "Autor(es)";
const CAMPO_EMAIL = "E-mail";
const CAMPO_REGULAMENTO = "Regulamento";

/**
 * Interface que representa uma ideia processada com dados enriquecidos para exibição na tabela
 */
export interface TIdeiaProcessada extends TIdeia {
  /** Nome do autor da ideia */
  autorNome: string;
  /** Email do autor da ideia */
  autorEmail: string;
  /** Número total de curtidas recebidas */
  curtidas: number;
  /** Número total de comentários recebidos */
  comentarios: number;
  /** Quantidade total de moedas associadas à ideia */
  moedas: number;
  /** Nome do regulamento associado à ideia */
  regulamentoNome: string;
}

/**
 * Interface para os resultados processados da query
 */
interface QueryResults {
  /** Dados dos cards de estatísticas */
  cards: Cards;
  /** Lista de ideias processadas com dados enriquecidos */
  ideiasProcessadas: TIdeiaProcessada[];
}

/**
 * Interface para parâmetros de filtro de busca
 */
interface SearchFilterParameters {
  /** Lista de ideias processadas */
  ideiasProcessadas: TIdeiaProcessada[];
  /** Termo de busca */
  searchTerm: string;
  /** Campo selecionado para busca */
  campo: string;
}

const defaultCards: Cards = {
  colaboradoresAtivos: 0,
  ideias: { total: 0, participantes: new Set(), porcentagem: 0 },
  curtidas: { total: 0, participantes: new Set(), porcentagem: 0 },
  comentarios: { total: 0, participantes: new Set(), porcentagem: 0 }
};

// Cache simples em memória
const colaboradorCache = new Map<string, TColaborador>();
const regulamentoCache = new Map<string, TRegulamentoIdeias>();

/**
 * Busca dados de um colaborador com cache em memória
 * @param path - Caminho de referência do documento do colaborador
 * @returns Promise que resolve com os dados do colaborador ou null se não encontrado
 */
async function getColaborador(path: string): Promise<TColaborador | null> {
  // eslint-disable-next-line security/detect-object-injection -- path is a controlled firestore document reference
  if (colaboradorCache.has(path)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- verified by has() check above
    return colaboradorCache.get(path)!;
  }
  try {
    const documentReference = getColaboradorDocumentReferenceFromPath(path);
    const snapshot = await getDoc(documentReference);
    if (snapshot.exists()) {
      const data = snapshot.data() as TColaborador;
      colaboradorCache.set(path, data);
      return data;
    }
  } catch (error) {
    console.error(ERRO_FETCH_COLABORADOR, error);
  }
  return null;
}

/**
 * Busca dados de um regulamento com cache em memória
 * @param path - Caminho de referência do documento do regulamento
 * @returns Promise que resolve com os dados do regulamento ou null se não encontrado
 */
async function fetchRegulamento(path: string): Promise<TRegulamentoIdeias | null> {
  // eslint-disable-next-line security/detect-object-injection -- path is a controlled firestore document reference
  if (regulamentoCache.has(path)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- verified by has() check above
    return regulamentoCache.get(path)!;
  }
  try {
    const documentReference = await getRegulamentoDocumentFromReferencePath(path);
    if (documentReference.exists()) {
      const data = documentReference.data();
      regulamentoCache.set(path, data);
      return data;
    }
  } catch (error) {
    console.error(ERRO_FETCH_REGULAMENTO, error);
  }
  return null;
}

/**
 * Helper function para calcular porcentagem de participação
 * @param participantes - Número de participantes
 * @param colaboradoresAtivos - Número total de colaboradores ativos
 * @returns Porcentagem de participação
 */
function calcularPorcentagemParticipacao(participantes: number, colaboradoresAtivos: number): number {
  return colaboradoresAtivos > 0 ? (participantes / colaboradoresAtivos) * CEM_PORCENTO : 0;
}

/**
 * Helper function para processar dados de comentários
 * @param comentariosSnaps - Array de snapshots dos comentários
 * @param colaboradoresAtivos - Lista de colaboradores ativos
 * @returns Objeto com dados processados dos comentários
 */
function processarDadosComentarios(comentariosSnaps: QuerySnapshot[], colaboradoresAtivos: TColaborador[]) {
  const comentarioParticipantes = new Set<string>();
  for (const snap of comentariosSnaps) {
    for (const document_ of snap.docs) {
      const documentData = document_.data() as { createdBy: string };
      comentarioParticipantes.add(documentData.createdBy);
    }
  }

  return {
    total: comentariosSnaps.reduce((accumulator, snap) => accumulator + snap.docs.length, 0),
    participantes: comentarioParticipantes,
    porcentagem: calcularPorcentagemParticipacao(comentarioParticipantes.size, colaboradoresAtivos.length)
  };
}

/**
 * Helper function para processar dados de curtidas
 * @param curtidasSnaps - Array de snapshots das curtidas
 * @param colaboradoresAtivos - Lista de colaboradores ativos
 * @returns Objeto com dados processados das curtidas
 */
function processarDadosCurtidas(
  curtidasSnaps: Array<Array<{ createdBy: string }>>,
  colaboradoresAtivos: TColaborador[]
) {
  const curtidaParticipantes = new Set<string>();
  for (const curtidas of curtidasSnaps) {
    for (const curtida of curtidas) {
      curtidaParticipantes.add(curtida.createdBy);
    }
  }

  return {
    total: curtidasSnaps.reduce((accumulator, curtidas) => accumulator + curtidas.length, 0),
    participantes: curtidaParticipantes,
    porcentagem: calcularPorcentagemParticipacao(curtidaParticipantes.size, colaboradoresAtivos.length)
  };
}

/**
 * Helper function para aplicar filtros de busca
 * @param params - Parâmetros de filtro
 * @param params.ideiasProcessadas
 * @param params.searchTerm
 * @param params.campo
 * @returns Lista filtrada de ideias
 */
function aplicarFiltrosBusca({ ideiasProcessadas, searchTerm, campo }: SearchFilterParameters): TIdeiaProcessada[] {
  return ideiasProcessadas.filter((item) => {
    switch (campo) {
      case CAMPO_TITULO: {
        return item.titulo.toLowerCase().includes(searchTerm);
      }
      case CAMPO_AUTORES: {
        return item.autorNome.toLowerCase().includes(searchTerm);
      }
      case CAMPO_EMAIL: {
        return item.autorEmail.toLowerCase().includes(searchTerm);
      }
      case CAMPO_REGULAMENTO: {
        return item.regulamentoNome.toLowerCase().includes(searchTerm);
      }
      default: {
        return true;
      }
    }
  });
}

/**
 * Fetches base ideas data including colaboradores, ideias, and all ideias
 * @param empresaId - Company ID
 * @param dateRange - Date range for filtering
 * @param dateRange.dataInicial
 * @param dateRange.dataFinal - Date range end
 * @param customFilters - Custom filters for ideas
 * @returns Base data object
 */
async function fetchBaseIdeiasData(
  empresaId: string,
  dateRange: { dataInicial: Date; dataFinal: Date },
  customFilters: FiltroIdeia
) {
  // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
  const [colaboradoresSnap, ideiasSnap, todasIdeiasSnap] = await Promise.all([
    getAllColaboradoresAtivosENaoExcluidos(empresaId),
    getIdeiasPublishedWithDateFilter(
      empresaId,
      dateRange.dataInicial,
      dateRange.dataFinal,
      customFilters.status as TIdeiaStatus | undefined
    ),
    // Buscar todas as ideias para comentários independentes
    getIdeiasPublishedWithDateFilter(empresaId, EPOCH_DATE, new Date())
  ]);

  return {
    colaboradoresAtivos: colaboradoresSnap.docs.map((document_) => document_.data()),
    ideias: ideiasSnap.docs.map((document_) => document_.data()),
    todasIdeias: todasIdeiasSnap.docs.map((document_) => document_.data())
  };
}

/**
 * Fetches aggregated ideas data including comments, likes, and regulations
 * @param empresaId - Company ID
 * @param baseData - Base data from fetchBaseIdeiasData
 * @param baseData.ideias
 * @param baseData.todasIdeias
 * @param dateRange - Date range for filtering
 * @param dateRange.dataInicial
 * @param dateRange.dataFinal
 * @returns Aggregated data object
 */
async function fetchAggregatedIdeiasData(
  empresaId: string,
  baseData: { ideias: TIdeia[]; todasIdeias: TIdeia[] },
  dateRange: { dataInicial: Date; dataFinal: Date }
) {
  const comentariosPromises = baseData.todasIdeias.map((index) =>
    getComentariosWithDateFilter(empresaId, index.id, dateRange.dataInicial, dateRange.dataFinal)
  );
  const curtidasPromises = baseData.ideias.map((index) => getLikes(empresaId, index.id));
  const moedasPromises = baseData.ideias.map((index) => getSumMoedasPerIdeia(empresaId, index.id));
  const regulamentoPromises = baseData.ideias.map((index) => fetchRegulamento(index.regulamentoReference));

  // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
  const [comentariosSnaps, curtidasSnaps, moedasResult, regulamentosResult] = await Promise.all([
    // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
    Promise.all(comentariosPromises),
    // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
    Promise.all(curtidasPromises),
    // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
    Promise.all(moedasPromises),
    // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
    Promise.all(regulamentoPromises)
  ]);

  return { comentariosSnaps, curtidasSnaps, moedasResult, regulamentosResult };
}

/**
 * Processes ideas data by combining base data with aggregated data
 * @param baseData - Base data containing colaboradores, ideias, and todasIdeias
 * @param baseData.colaboradoresAtivos
 * @param baseData.ideias
 * @param baseData.todasIdeias
 * @param aggregatedData - Aggregated data containing comments, likes, moedas, and regulamentos
 * @param aggregatedData.comentariosSnaps
 * @param aggregatedData.curtidasSnaps
 * @param aggregatedData.moedasResult
 * @param aggregatedData.regulamentosResult
 * @returns Processed data with cards and ideiasProcessadas
 */
async function processIdeiasData(
  baseData: { colaboradoresAtivos: TColaborador[]; ideias: TIdeia[]; todasIdeias: TIdeia[] },
  aggregatedData: {
    comentariosSnaps: QuerySnapshot[];
    curtidasSnaps: Array<{ createdBy: string }>[];
    moedasResult: Array<number | { data: () => { moedas: number } }>;
    regulamentosResult: Array<TRegulamentoIdeias | null>;
  }
): Promise<QueryResults> {
  const colaboradoresMap = new Map(baseData.colaboradoresAtivos.map((c) => [c.id, c]));
  const comentariosPorIdeia = new Map(
    baseData.todasIdeias.map((index, index_) => [
      index.id,
      // eslint-disable-next-line security/detect-object-injection -- index_ is a controlled array index
      aggregatedData.comentariosSnaps[index_]?.docs ?? []
    ])
  );
  const curtidasPorIdeia = new Map(
    baseData.ideias.map((index, index_) => [
      index.id,
      // eslint-disable-next-line security/detect-object-injection -- index_ is a controlled array index
      aggregatedData.curtidasSnaps[index_] ?? []
    ])
  );
  const moedasPorIdeia = new Map(
    baseData.ideias.map((index, index_) => {
      // eslint-disable-next-line security/detect-object-injection -- index_ is a controlled array index
      const moedasData = aggregatedData.moedasResult[index_] ?? 0;
      const moedas =
        typeof moedasData === "number"
          ? moedasData
          : ((moedasData as { data: () => { moedas: number } })?.data().moedas ?? 0);
      return [index.id, moedas];
    })
  );
  const regulamentoPorIdeia = new Map(
    baseData.ideias.map((index, index_) => [
      index.id,
      // eslint-disable-next-line security/detect-object-injection -- index_ is a controlled array index
      aggregatedData.regulamentosResult[index_] ?? null
    ])
  );

  const cards = { ...defaultCards, colaboradoresAtivos: baseData.colaboradoresAtivos.length };
  const ideiaParticipantes = new Set(baseData.ideias.map((index) => index.createdBy));
  cards.ideias = {
    total: baseData.ideias.length,
    participantes: ideiaParticipantes,
    porcentagem: calcularPorcentagemParticipacao(ideiaParticipantes.size, baseData.colaboradoresAtivos.length)
  };

  cards.comentarios = processarDadosComentarios(aggregatedData.comentariosSnaps, baseData.colaboradoresAtivos);
  cards.curtidas = processarDadosCurtidas(aggregatedData.curtidasSnaps, baseData.colaboradoresAtivos);

  const ideiasProcessadasPromises = baseData.ideias.map(async (ideia) => {
    const autor = colaboradoresMap.get(ideia.createdBy) ?? (await getColaborador(ideia.autorReference));
    const regulamento = regulamentoPorIdeia.get(ideia.id);
    const curtidas = curtidasPorIdeia.get(ideia.id);
    const comentarios = comentariosPorIdeia.get(ideia.id);
    const moedas = moedasPorIdeia.get(ideia.id);

    return {
      ...ideia,
      autorNome: autor?.nome ?? VALOR_NAO_ENCONTRADO,
      autorEmail: autor?.email ?? VALOR_NAO_ENCONTRADO,
      curtidas: curtidas?.length ?? 0,
      comentarios: comentarios?.length ?? 0,
      moedas: moedas ?? 0,
      regulamentoNome: regulamento?.nome ?? VALOR_NAO_ENCONTRADO
    };
  });

  // eslint-disable-next-line compat/compat -- Promise.all is supported in target environments
  const ideiasProcessadas = await Promise.all(ideiasProcessadasPromises);
  return { cards, ideiasProcessadas };
}

/**
 * Hook personalizado para gerenciar dados do relatório de ideias
 * @param dateRange - Objeto contendo as datas inicial e final do período
 * @param dateRange.dataInicial - Data inicial do período
 * @param dateRange.dataFinal - Data final do período
 * @param customFilters - Filtros personalizados para as ideias
 * @returns Objeto contendo os dados processados, estado de carregamento e funções auxiliares
 */
export function useIdeiasRelatorio(dateRange: { dataInicial: Date; dataFinal: Date }, customFilters: FiltroIdeia) {
  const empresa = useUserStore((state) => state.activeEmpresa);
  const [isProcessing, setIsProcessing] = useState(true);

  const queryKey = useMemo(
    () => ["ideiasRelatorio", empresa?.id ?? "", dateRange, customFilters] as const,
    [empresa?.id, dateRange, customFilters]
  );

  const { data, isLoading: isLoadingQuery } = useQuery<QueryResults>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async (): Promise<QueryResults> => {
      const empresaId = empresa?.id ?? "";
      if (!empresaId) throw new Error("ID da empresa não encontrado.");
      setIsProcessing(true);

      const baseData = await fetchBaseIdeiasData(empresaId, dateRange, customFilters);
      const aggregatedData = await fetchAggregatedIdeiasData(empresaId, baseData, dateRange);

      return await processIdeiasData(baseData, aggregatedData);
    },
    enabled: !!empresa?.id,
    staleTime: STALE_TIME_MS
  });

  const dadosFiltrados = useMemo(() => {
    if (!data?.ideiasProcessadas) return [];
    if (!customFilters.search || !customFilters.campo) return data.ideiasProcessadas;

    const searchTerm = customFilters.search.toLowerCase();
    return aplicarFiltrosBusca({
      ideiasProcessadas: data.ideiasProcessadas,
      searchTerm,
      campo: customFilters.campo
    });
  }, [data?.ideiasProcessadas, customFilters.search, customFilters.campo]);

  useEffect(() => {
    if (!isLoadingQuery) setIsProcessing(false);
  }, [isLoadingQuery]);

  return {
    /** Dados dos cards de estatísticas */
    cards: data?.cards ?? defaultCards,
    /** Lista de ideias processadas com filtros aplicados */
    ideiasProcessadas: dadosFiltrados,
    /** Estado de carregamento combinado da query e processamento */
    isLoading: isLoadingQuery || isProcessing
  };
}
